import { useEffect } from 'react';

import { paletteState } from 'core/state';
import { isEditableTarget } from 'core/utils/isEditableTarget';

/**
 * Global keyboard entry points for the command palette:
 *  - `Cmd/Ctrl+K` toggles the palette (works even while typing — the hard
 *    default for Phase 1; `Ctrl+P` is skipped to avoid the browser-print clash).
 *  - `?` (Shift+/) opens the read-only shortcuts cheat sheet, but ONLY when the
 *    operator is not typing in a field and no overlay is already open.
 *
 * SSR-guarded and cleaned up on unmount (same `addEventListener` idiom as
 * `GroupDetail/parts/SyncMenu`).
 */
export const useGlobalHotkey = (
  toggle: () => void,
  openHelp?: () => void,
): void => {
  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        toggle();
        return;
      }

      if (
        openHelp &&
        event.key === '?' &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey &&
        !isEditableTarget(event.target) &&
        !paletteState().open
      ) {
        event.preventDefault();
        openHelp();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [toggle, openHelp]);
};
